/** @jsxImportSource @emotion/react */
import { faPenToSquare } from "@fortawesome/pro-regular-svg-icons";
import { useEffect, useState } from "react";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";

import { applyDisplayFilterFields } from "../../../../actions/Tenants/config/applyDisplayFilter";
import useConfig from "../../../../actions/Tenants/config/configHook";
import MetaForm, { metaFormIsValid } from "../../../Forms/MetaForm";
import { Button } from "../../../Helper/Button";
import useRipeningRoom from "../hooks/useRipeningRoom";

export default function AddRipeningCycleNoteForm({ isInBottomBar }: { isInBottomBar?: boolean }) {
    const config = useConfig();
    const { fruit_type, activeRipeningCycle, editRipeningCycle, editRipeningCycleLoading, viewingActiveCycle } = useRipeningRoom();
    const [modalOpen, setModalOpen] = useState(false);
    const [query, setQuery] = useState<any>({});

    const editCycleFields = applyDisplayFilterFields(config.root_config?.ripening_room_edit_cycle_form, { fruit_type });
    const roomNotesField = editCycleFields.filter((f) => f?.name === "room_notes");

    useEffect(() => {
        if (!modalOpen) {
            return;
        }
        // Reset when opening; notes are append-like, not an edit-in-place UX.
        setQuery({ room_notes: "" });
    }, [modalOpen, activeRipeningCycle?.id]);

    const setValue = (field: string, value: any) => setQuery((prev) => ({ ...prev, [field]: value }));

    const onDebounce = (field: string, value: any) => {
        const field_config = roomNotesField.find((i) => i.name === field);
        if (metaFormIsValid([field_config], { [field]: value })) {
            return setQuery((prev) => ({ ...prev, [field]: value }));
        }
        return Promise.resolve();
    };

    const onSubmit = () => {
        if (!activeRipeningCycle) return;
        if (roomNotesField && !metaFormIsValid(roomNotesField, query)) return;
        editRipeningCycle({ room_notes: query.room_notes });
        setModalOpen(false);
    };

    // Only show if viewing the active cycle (keep hooks order stable)
    if (!viewingActiveCycle) {
        return null;
    }

    return (
        <>
            <Modal isOpen={modalOpen} size="md">
                <ModalHeader toggle={() => setModalOpen(false)}>Add note</ModalHeader>
                <ModalBody>
                    {(() => {
                        if (roomNotesField.length === 0) {
                            return (
                                <div className="text-muted small">
                                    The `room_notes` field is not configured in the edit cycle form.
                                </div>
                            );
                        }

                        return (
                            <MetaForm
                                meta={roomNotesField}
                                object={query}
                                config={config}
                                setValue={setValue}
                                onDebounce={onDebounce}
                                extra_context={{ fruit_type: fruit_type || undefined }}
                            />
                        );
                    })()}
                </ModalBody>
                <ModalFooter>
                    <Button color="light" onClick={() => setModalOpen(false)}>Cancel</Button>
                    <Button color="primary" icon={faPenToSquare} onClick={onSubmit} disabled={editRipeningCycleLoading || roomNotesField.length === 0}>
                        Save note
                    </Button>
                </ModalFooter>
            </Modal>

            <Button
                className="text-nowrap"
                color="primary"
                onClick={() => setModalOpen(true)}
                icon={faPenToSquare}
                isInBottomBar={isInBottomBar}
            >
                Note
            </Button>
        </>
    );
}

